import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button } from 'semantic-ui-react';
import styled from '@emotion/styled';
import { screenSizes } from 'src/assets/styles/Literals';

// Bundle the worker via Webpack so it matches the installed pdfjs-dist version.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

const MAX_PAGE_WIDTH = 800;

const PDFContainer = styled.div`
  width: 100%;
  position: relative;
  padding-bottom: 96px;

  .status-message {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    padding: 24px;
    text-align: center;
    color: #555;
    font-size: 14px;
  }

  .status-message.error {
    color: #b00020;
  }

  .document-shell {
    width: 100%;
    max-width: ${MAX_PAGE_WIDTH}px;
    margin: 0 auto;
  }

  .actions-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    z-index: 10;
    padding: 12px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    width: min(calc(100% - 24px), 420px);

    @media (max-width: ${screenSizes.tablet}) {
      flex-direction: column;
      gap: 12px;
      width: min(calc(100% - 24px), 320px);
    }

    .goto-page {
      display: flex;
      flex-direction: row;
      align-items: center;
      font-size: 14px;

      @media (max-width: ${screenSizes.tablet}) {
        font-size: 13px;
        justify-content: center;
        width: 100%;
      }

      input {
        width: 56px;
        height: 32px;
        margin: 0 8px;
        text-align: center;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
      }
    }

    .navigation-buttons {
      display: flex;
      gap: 8px;

      @media (max-width: ${screenSizes.tablet}) {
        width: 100%;
        justify-content: center;
      }

      .ui.button {
        min-width: 80px;
        font-size: 13px;
      }
    }
  }

  .react-pdf__Document {
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 12px 0 20px;
  }

  .react-pdf__Page {
    max-width: 100%;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
  }

  .react-pdf__Page__canvas {
    max-width: 100%;
    height: auto !important;
  }
`;

const PDFReader = ({ url }) => {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(MAX_PAGE_WIDTH);
  const [loading, setLoading] = useState(Boolean(url));
  const [error, setError] = useState(null);

  useEffect(() => {
    setNumPages(null);
    setPageNumber(1);
    setLoading(Boolean(url));
    setError(null);
  }, [url]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateWidth = () => {
      const nextWidth = Math.min(Math.floor(node.clientWidth), MAX_PAGE_WIDTH);
      if (nextWidth > 0) {
        setPageWidth(nextWidth);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [url]);

  if (!url) {
    return (
      <PDFContainer>
        <div className="status-message error">No hay un PDF disponible para esta lección.</div>
      </PDFContainer>
    );
  }

  const handleLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setPageNumber(1);
    setLoading(false);
    setError(null);
  };

  const handleLoadError = (loadError) => {
    console.error('Error loading PDF:', loadError);
    setLoading(false);
    setError('No se pudo cargar el PDF. Usa el botón de descarga para abrirlo.');
  };

  const handlePageChange = (page) => {
    if (!numPages) return;
    if (page > numPages) {
      setPageNumber(numPages);
    } else if (page < 1) {
      setPageNumber(1);
    } else {
      setPageNumber(page);
    }
  };

  return (
    <PDFContainer>
      <div className="document-shell" ref={containerRef}>
        {loading && <div className="status-message">Cargando PDF...</div>}
        {error && <div className="status-message error">{error}</div>}
        {!error && (
          <Document
            file={url}
            loading={null}
            onLoadSuccess={handleLoadSuccess}
            onLoadError={handleLoadError}
          >
            <Page pageNumber={pageNumber} width={pageWidth} renderAnnotationLayer renderTextLayer />
          </Document>
        )}
      </div>

      {!error && numPages > 0 && (
        <div className="actions-container">
          <div className="goto-page">
            Página{' '}
            <input
              type="number"
              value={pageNumber}
              onChange={(e) => handlePageChange(Number(e.target.value) || 1)}
              min={1}
              max={numPages}
            />{' '}
            de {numPages}
          </div>

          <div className="navigation-buttons">
            <Button disabled={pageNumber <= 1} onClick={() => handlePageChange(pageNumber - 1)}>
              Anterior
            </Button>
            <Button
              disabled={pageNumber >= numPages}
              onClick={() => handlePageChange(pageNumber + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </PDFContainer>
  );
};

export { PDFReader };
